import { createPopper, Instance } from "@popperjs/core";
import useScreenSize from "hoc/useScreenSize";
import useOutsideClick from "hooks-contexts/useOutsideClick";
import { useRouter } from "next/router";
import {
  cloneElement, MouseEvent, useEffect, useRef, useState,
} from "react";
import popoverStyles from "./popover.module.scss";

function Popover({
  component, overlay, children, placement, className = "", popoverContentClassName = "", defaultOpen = false,
}: any) {
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef<any>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const popperInstance = useRef<Instance | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [, , isDesktop] = useScreenSize();
  const router = useRouter();

  useOutsideClick(tooltipRef, (e: MouseEvent) => {
    if (!btnRef.current?.contains(e.target)) close();
  }, !isOpen);

  useEffect(() => {
    if (!btnRef.current || !tooltipRef.current || !isOpen) return;
    const instance = createPopper(btnRef.current, tooltipRef.current, {
      placement: placement ?? "bottom",
      modifiers: [{ name: "offset", options: { offset: [0, 0] } }],
    });
    popperInstance.current = instance;

    // ResizeObserver is used to update the popper position for home page because popover is open by default.
    const observer = new ResizeObserver(() => instance.update());
    observer.observe(tooltipRef.current);

    return () => {
      observer.disconnect();
      instance.destroy();
      popperInstance.current = null;
    };
  }, [isOpen, placement]);

  useEffect(() => {
    if (defaultOpen) {
      setTimeout(() => {
        open();
      }, 100);

      // Close the popover after 5 seconds if it's the homepage to prevent it from staying open indefinitely
      setTimeout(() => {
        close();
      }, 5000);
    }
  }, []);

  useEffect(() => {
    if (isOpen) popperInstance.current?.update();
  }, [isOpen]);

  function cancelClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }

  function close() { cancelClose(); setIsOpen(false); }

  function open() {
    cancelClose();
    setIsOpen(true);
  }

  function scheduleClose() {
    cancelClose();
    closeTimer.current = setTimeout(() => setIsOpen(false), 0);
  }

  useEffect(() => {
    router.events.on("routeChangeComplete", close);
    return () => router.events.off("routeChangeComplete", close);
  }, []);

  const renderItem = cloneElement(children, {
    onMouseEnter: isDesktop ? open : undefined,
    onMouseLeave: isDesktop ? scheduleClose : undefined,
    onClick: () => (isOpen ? setIsOpen(false) : open()),
    ref: btnRef,
  });

  return (
    <div className={`${popoverStyles.styleContainer} ${className}`}>
      <div className={popoverStyles.popover__wrapper}>
        {renderItem}
        <div
          ref={tooltipRef}
          onMouseEnter={isDesktop ? cancelClose : undefined}
          onMouseLeave={isDesktop ? scheduleClose : undefined}
          className={`${popoverStyles.popover__content} ${isOpen ? popoverStyles.visible : ""} ${popoverContentClassName}`}
        >
          <div data-popper-arrow className={popoverStyles.arrow} />
          {overlay || component}
        </div>
      </div>
    </div>
  );
}

export default Popover;
